import { z } from 'zod';
import type {
  ContentBundle,
  AssetManifest,
  Ingredient,
  Recipe,
  Topping,
  Customer,
  Decoration,
  Equipment,
} from '../types';

const baseUnit = z.enum(['tbsp', 'tsp', 'cup', 'egg', 'oz', 'count']);
const fraction = z.enum(['1/4', '1/3', '1/2', '2/3', '3/4']);
const itemType = z.enum(['cupcake', 'cookie', 'bread', 'pie', 'cake', 'roll', 'muffin']);

const measure = z.object({
  qty: z.number(),
  fraction: fraction.optional(),
  unit: baseUnit,
});

const ingredientSchema = z.object({
  id: z.string(),
  name: z.string(),
  sprite: z.string(),
  unitCost: z.number(),
  baseUnit,
}) satisfies z.ZodType<Ingredient>;

const recipeSchema = z.object({
  id: z.string(),
  name: z.string(),
  itemType,
  flavor: z.string(),
  tempF: z.number(),
  minutes: z.number(),
  yield: z.number().int().positive(),
  ingredients: z.array(z.object({ ingredient: z.string(), measure })),
  allowedToppings: z.array(z.string()),
  unlockScore: z.number().optional(),
  sprite: z.string().optional(),
}) satisfies z.ZodType<Recipe>;

const toppingSchema = z.object({
  id: z.string(),
  name: z.string(),
  flavor: z.string(),
  sprite: z.string().optional(),
}) satisfies z.ZodType<Topping>;

const customerSchema = z.object({
  id: z.string(),
  name: z.string(),
  sprite: z.string().optional(),
  prefs: z.object({
    flavors: z.array(z.string()),
    itemTypes: z.array(itemType),
    favoriteTopping: z.string().optional(),
    dislikedIngredient: z.string().optional(),
    priceTolerance: z.number(),
  }),
  visits: z.number().default(0),
  cumulativeSatisfaction: z.number().default(0),
}) satisfies z.ZodType<Customer>;

const decorationSchema = z.object({
  id: z.string(),
  sprite: z.string().optional(),
  cost: z.number(),
  placement: z
    .object({ x: z.number(), y: z.number(), scene: z.enum(['Counter', 'Kitchen']) })
    .optional(),
}) satisfies z.ZodType<Decoration>;

const equipmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  sprite: z.string().optional(),
  cost: z.number(),
  unlocksRecipes: z.array(z.string()),
}) satisfies z.ZodType<Equipment>;

const manifestSchema = z.object({
  generatedAt: z.string(),
  sourceProject: z.string().optional(),
  counts: z.object({ images: z.number(), audio: z.number() }),
  images: z.record(z.string(), z.string()),
  audio: z.record(z.string(), z.string()),
}) satisfies z.ZodType<AssetManifest>;

async function fetchJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Content load failed: ${path} (${res.status})`);
  const raw = await res.json();
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    console.error(`Content validation failed for ${path}:`, parsed.error.issues);
    throw new Error(`Invalid content in ${path}`);
  }
  return parsed.data;
}

export async function loadAll(): Promise<{ content: ContentBundle; manifest: AssetManifest }> {
  const [recipes, ingredients, toppings, customers, decorations, equipment, manifest] =
    await Promise.all([
      fetchJson('content/recipes.json', z.array(recipeSchema)),
      fetchJson('content/ingredients.json', z.array(ingredientSchema)),
      fetchJson('content/toppings.json', z.array(toppingSchema)),
      fetchJson('content/customers.json', z.array(customerSchema)),
      fetchJson('content/decorations.json', z.array(decorationSchema)),
      fetchJson('content/equipment.json', z.array(equipmentSchema)),
      fetchJson('assets/manifest.json', manifestSchema),
    ]);
  return {
    content: { recipes, ingredients, toppings, customers, decorations, equipment },
    manifest,
  };
}
